import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import SecFilingDataTableHead from './SecFilingDataTableHead';
import SecFilingTableRow from './SecFilingTableRow';
import { hasSecFilingFiltersApplied } from '../utils/secFilingFilters';

export default function SecFilingInsightsPeriodTable({
  period,
  rangeLabel,
  rows: rowsProp = [],
  periodSwitchVersion = 0,
  filters,
  companyFilter = null,
  companyFilterLabel = null,
  subjectCompany = '',
  onClearCompanyFilter,
  currentPage: currentPageProp = 1,
  onPageChange,
}) {
  const navigate = useNavigate();
  const {
    selectedCompanies,
    selectedFormTypes,
    selectedXbrlTags,
    filedStart,
    filedEnd,
  } = filters;

  const rows = rowsProp;

  const drawerFiltersApplied = hasSecFilingFiltersApplied({
    selectedCompanies,
    selectedFormTypes,
    selectedXbrlTags,
    filedStart,
    filedEnd,
  });

  const [currentPage, setCurrentPage] = useState(currentPageProp);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pageInput, setPageInput] = useState('1');
  const [openFilter, setOpenFilter] = useState(null);

  useEffect(() => {
    setCurrentPage(currentPageProp);
    setPageInput(String(currentPageProp));
  }, [currentPageProp]);

  useEffect(() => {
    setCurrentPage(1);
    setPageInput('1');
    onPageChange?.(1);
  }, [period.key, companyFilter, selectedCompanies, selectedFormTypes, selectedXbrlTags, onPageChange]);

  const setPage = useCallback((next) => {
    setCurrentPage((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      onPageChange?.(resolved);
      return resolved;
    });
  }, [onPageChange]);

  const totalRows = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const safePage = Math.min(currentPage, totalPages);

  const displayedRows = useMemo(() => {
    const start = (safePage - 1) * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, safePage, rowsPerPage]);

  const handleRowSelect = useCallback((filingId, row) => {
    const tableParams = new URLSearchParams();
    const company = companyFilter || row?.company;
    if (company) tableParams.set('company', company);
    if (period?.key) tableParams.set('period', period.key);
    if (filingId) tableParams.set('filing', filingId);
    navigate(`/sec-filing/table?${tableParams.toString()}`);
  }, [companyFilter, navigate, period?.key]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setPage(totalPages);
    }
    setPageInput(String(Math.min(currentPage, totalPages)));
  }, [currentPage, totalPages, setPage]);

  const handlePageInputSubmit = useCallback((event) => {
    event.preventDefault();
    const next = parseInt(pageInput, 10);
    if (!Number.isNaN(next) && next >= 1 && next <= totalPages) {
      setPage(next);
    } else {
      setPageInput(String(safePage));
    }
  }, [pageInput, safePage, totalPages]);

  const startIndex = totalRows === 0 ? 0 : (safePage - 1) * rowsPerPage;

  return (
      <section
        className="sec-filing-insights-period-table"
        aria-label="All filings in selected period"
      >
        <div className="sec-filing-insights-period-table__header">
          <div className="sec-filing-insights-period-table__header-main">
            <h2 className="sec-filing-insights-period-table__title">
              {companyFilterLabel ? `${companyFilterLabel} filings` : 'All filings'}
            </h2>
            {companyFilter ? (
              <button
                type="button"
                className="sec-filing-insights-period-table__clear-filter"
                onClick={onClearCompanyFilter}
              >
                Show all
              </button>
            ) : null}
          </div>
        </div>

        <div
          key={periodSwitchVersion}
          className={[
            'sec-filing-insights-period-table__panel',
            periodSwitchVersion > 0 ? 'sec-filing-insights-period-table__panel--period-switch' : '',
          ].filter(Boolean).join(' ')}
        >
          <div className="table-wrap sec-filing-insights-period-table__wrap">
            <table className="sec-filing-insights-data-table">
              <colgroup>
                <col className="col-sno" />
                <col className="col-company" />
                <col className="col-entity" />
                <col className="col-type" />
                <col className="col-priority" />
                <col className="col-accepted" />
                <col className="col-date" />
                <col className="col-file-film" />
              </colgroup>
              <SecFilingDataTableHead />
              <tbody>
                {displayedRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="sec-filing-insights-period-table__empty">
                      {drawerFiltersApplied
                        ? 'No SEC filings match your filters in this period.'
                        : companyFilter
                          ? `No SEC filings for ${companyFilterLabel || companyFilter} in this period.`
                          : 'No SEC filings in this period.'}
                    </td>
                  </tr>
                ) : (
                  displayedRows.map((row, rowIdx) => (
                    <SecFilingTableRow
                      key={row.id}
                      row={row}
                      rowIndex={rowIdx}
                      startIndex={startIndex}
                      subjectCompany={subjectCompany}
                      onSelect={(filingId) => handleRowSelect(filingId, row)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination pagination-sticky sec-filing-insights-period-table__pagination">
            <div className="pg-info">
              Showing {totalRows === 0 ? 0 : startIndex + 1}–{Math.min(safePage * rowsPerPage, totalRows)} of {totalRows} filings
            </div>
            <div className="table-pagination">
              <div className="table-pagination__control">
                <span className="table-pagination__label">Rows per page</span>
                <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
                  <span className="select-measure table-pagination__measure">{rowsPerPage}</span>
                  <select
                    className="filter-select table-pagination__select"
                    value={rowsPerPage}
                    onFocus={() => setOpenFilter('rows')}
                    onBlur={() => setOpenFilter(null)}
                    onChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(1);
                      e.target.blur();
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                  </select>
                </div>
              </div>
              <div className="pg-controls">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft size={14} />
                </button>
                <form onSubmit={handlePageInputSubmit} className="companydetails-style-33">
                  <input
                    type="text"
                    className="pg-input"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onBlur={() => setPageInput(String(safePage))}
                  />
                  <button type="submit" style={{ display: 'none' }} />
                </form>
                <button
                  type="button"
                  className="pg-btn"
                  disabled={safePage === totalPages || totalRows === 0}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
  );
}
